// SOW items D and E on the home page.
//
//   D — Critical spares coverage: how many of the parts AMF designates Critical for this site's
//       machines the site has actually ever ordered, broken down per machine.
//   E — Due for reorder: parts this site buys on a rhythm and is now due to buy again.
//
// WHY PER MACHINE RATHER THAN ONE SITE-WIDE BAR. The wireframe showed "12 of 18 Critical spares".
// The real figure at C.H. Guenther Coventry is 623 of 853 across 36 machines — a single bar over 853
// parts is a statistic, not something a maintenance manager can act on. The machine with the biggest
// gap is the useful unit.
//
// RENDERS NOTHING WHEN THERE IS NOTHING TO SAY. A site with no rhythm history and no Critical gaps
// gets no panel at all, rather than an empty state explaining its own absence — the same rule the
// part-page panel follows.

import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
	Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, Link, Paper, Stack,
	Tooltip, Typography,
} from "@mui/material";
import {
	AddShoppingCart, ExpandLess, ExpandMore, FileDownloadOutlined, OpenInNew, Summarize,
	TipsAndUpdates,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import { useCart } from "../../context/cartContext";
import { toast } from "../../context/ToastContext";
import { getCriticalCoverage, getDueForReorder, getMachineTopParts } from "../../services/partRecommendationService";
import ContactCCRForm from "../ContactCCRForm/ContactCCRForm";
import { saveRequestList } from "../../services/productCardService";
import formatCurrency from "../../utilities/currency";
import {
	getMinimumSalesOrderQuantity, getMultipleSalesOrderQuantity, normalizeOrderQuantity,
} from "../../utilities/orderQuantity";

// UAT 2026-08-17: price + Add to Cart on each recommendation row (items D and E), so a part worth
// acting on can be acted on right there instead of requiring a click-through first. Same
// purchasable/request-list fallback rule as TopProductListItem, since these rows are the same kind
// of "add this to your cart" action, just in a list instead of a card.
const PriceAndCartAction = ({ row, selectedSite, stacked = false }) => {
	const { user, canViewProductPrice, canAddToCart, canAddToRequestList } = useAuth();
	const { addToCart } = useCart();
	const [busy, setBusy] = useState(false);
	const [done, setDone] = useState(false);

	const canShowPrice = canViewProductPrice();
	const isPurchasable = Number(row.purchasable) === 1;
	// Cart and Request List have different backend requirements, checked directly rather than
	// assumed: cartContext.js's addToCart -> cart.controller.js's normalizeCartItems treats
	// machine_id as OPTIONAL (normalizeOptionalCartMachineId), but requestlist.controller.js
	// hard-rejects a missing machine_id (`if (!user_id || !site_number || !machine_id ||
	// !product_id)`). A part with no machine tie — a generic consumable not in any BOM, e.g.
	// "Lot Boulons/Bolts SALES ONLY" — can still be added to cart; it just can't go on a request list.
	const hasProduct = Boolean(row.product_id);
	const hasMachine = Boolean(row.machine_id);

	const handleAddToCart = async (event) => {
		event.stopPropagation();
		if (!hasProduct) return;
		setBusy(true);
		try {
			const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(row);
			const multipleSalesOrderQuantity = getMultipleSalesOrderQuantity(row);
			await addToCart({
				product_id: row.product_id,
				ProductID: row.product_id,
				machine_id: row.machine_id,
				MachineID: row.machine_id,
				product_number: row.product_number,
				product_name: row.description || row.product_number,
				Quantity: normalizeOrderQuantity(1, row),
				Site: selectedSite,
				minimum_sales_order_quantity: minimumSalesOrderQuantity,
				multiple_sales_order_quantity: multipleSalesOrderQuantity,
			});
			setDone(true);
			toast.trigger({
				message: `${row.product_number} added to cart`,
				severity: "success",
			});
			setTimeout(() => setDone(false), 2000);
		} catch (e) {
			toast.trigger({ message: `Error adding ${row.product_number} to cart`, severity: "error" });
		} finally {
			setBusy(false);
		}
	};

	const handleAddToRequestList = async (event) => {
		event.stopPropagation();
		if (!hasProduct || !hasMachine || !user) return;
		setBusy(true);
		try {
			const response = await saveRequestList({
				machine_id: row.machine_id,
				product_id: row.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			});
			toast.trigger({
				message: response?.status
					? `${row.product_number} added to your request list`
					: `${row.product_number} is already on your request list`,
				severity: response?.status ? "success" : "warning",
			});
		} catch (e) {
			toast.trigger({ message: `Error adding ${row.product_number} to request list`, severity: "error" });
		} finally {
			setBusy(false);
		}
	};

	if (!hasProduct || row.price === null || row.price === undefined) return null;

	return (
		<Box sx={{
			display: "flex",
			flexDirection: stacked ? "column" : "row",
			alignItems: stacked ? "flex-end" : "center",
			gap: stacked ? 0.25 : 0.75,
			flexShrink: 0,
		}}>
			{canShowPrice && isPurchasable && (
				<Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "var(--primary-main)" }}>
					{formatCurrency(row.price, row.currency)}
				</Typography>
			)}
			{isPurchasable && canAddToCart() ? (
				<Tooltip title="Add to cart">
					<IconButton size="small" color="primary" disabled={busy} onClick={handleAddToCart}>
						{done ? <AddShoppingCart fontSize="small" color="success" /> : <AddShoppingCart fontSize="small" />}
					</IconButton>
				</Tooltip>
			) : !isPurchasable && hasMachine && canAddToRequestList() ? (
				<Tooltip title="Add to request list">
					<IconButton size="small" color="primary" disabled={busy} onClick={handleAddToRequestList}>
						<Summarize fontSize="small" />
					</IconButton>
				</Tooltip>
			) : null}
		</Box>
	);
};

const SectionHeading = ({ title, subtitle }) => (
	<Box sx={{ mb: 1.2 }}>
		<Typography sx={{ fontWeight: 700, fontSize: "15px" }}>{title}</Typography>
		<Box sx={{ width: 64, height: "3px", backgroundColor: "var(--primary-main)", my: 0.4 }} />
		{subtitle && (
			<Typography sx={{ fontSize: "11.5px", color: "text.secondary" }}>{subtitle}</Typography>
		)}
	</Box>
);

// panelOpen: controlled from the home page's Recommendations tile — this component always loads its
// data (so the tile's badge count is accurate) but only renders its panels while panelOpen is true.
// Named to avoid colliding with this component's own "expanded" state (which machine row is open).
// onCountChange: reports how many items need attention (due-for-reorder rows + machines with an RSPL
// gap) so the tile can show that number, independent of whether the panel itself is open.
const SparesInsights = ({ panelOpen = true, onCountChange = null }) => {
	const { t } = useTranslation();
	const { selectedSite } = useSite();
	const navigate = useNavigate();
	// The row whose part has no machine and so cannot be opened anywhere — see openPart. Holding the
	// row itself, not a boolean, so the form can name the part.
	const [contactFor, setContactFor] = useState(null);

	// Both panels name parts the customer is meant to act on, so every row that CAN be opened is a
	// link. Rows whose part is missing from the catalogue have no ids and stay as plain text rather
	// than becoming a link that goes nowhere.
	// The part page route is /machines/:machineID/:productID — it needs BOTH ids.
	//
	// A part can legitimately have no machine: "Due for reorder" is built from what the site actually
	// buys, and lubricants, belts and other consumables are ordered without being in any machine's
	// bill of materials, so the query resolves machine_id to null. At C.H. Guenther Coventry that was
	// 3 of the 6 rows — half the panel was inert, which is what AMF reported as "some items
	// non-clickable" / "unable to click item" (2026-08-14).
	//
	// WHERE A MACHINE-LESS ROW GOES, and why it is not a page.
	//
	// "Bun Blades" (HANSA193H27-1/2) is the worked example. It is in the products catalogue and this
	// bakery has bought it ten times, but it appears in NO bill of materials anywhere — 0 rows in
	// boms — so there is no machine to hang it on. That rules out every part surface in the app:
	//
	//   - the part page is /machines/:machineID/:productID and needs a machine;
	//   - global search inner-joins products -> boms -> sites_machines, so a part in no BOM can never
	//     appear in results. That is why the search fallback showed "Parts 0" (reported 2026-08-17);
	//   - order history has no URL parameter that seeds its search box.
	//
	// The last two are fixable but live in shared, out-of-scope files, so they are written up for the
	// platform team rather than changed here.
	//
	// So the row opens the Customer Care form instead, naming the part. That is the app's own answer
	// for a part it cannot sell directly, it is a real path to actually getting the part, and it is
	// honest — unlike a link to a page that cannot show it.
	const openPart = (machineId, productId, row) => {
		// ROUTES.MACHINES is already "/machines". Prefixing another slash produced "//machines/…",
		// which a browser reads as a PROTOCOL-RELATIVE url — host "machines" — and fails with
		// ERR_SSL_VERSION_OR_CIPHER_MISMATCH rather than a 404. Matches how Rspl.js and Requests.js
		// build the same link.
		if (machineId && productId) return () => navigate(`${ROUTES.MACHINES}/${machineId}/${productId}`);
		if (row?.product_number) return () => setContactFor(row);
		return null;
	};
	const rowSx = (onClick) => (onClick
		? { cursor: "pointer", borderRadius: "3px", "&:hover": { backgroundColor: "#f5f6f8" } }
		: {});
	// A machine row opens that machine's parts list, where the missing Critical spares actually live.
	const openMachine = (machineId) => (machineId ? () => navigate(`${ROUTES.MACHINES}/${machineId}`) : null);
	const [loading, setLoading] = useState(true);
	const [coverage, setCoverage] = useState(null);
	const [due, setDue] = useState([]);
	const [topByMachine, setTopByMachine] = useState([]);
	const [failed, setFailed] = useState(false);
	// Which machine's gap list is open. One at a time — this panel sits on the home page and must not
	// grow into a second screen.
	const [expanded, setExpanded] = useState(null);
	// Both panels show a short list by default and reveal the rest in place. Neither opens a new page:
	// there is no "all parts due" route, and the previous attempt at a full-list link sent people to a
	// machine's entire 1,387-part catalogue.
	const [showAllDue, setShowAllDue] = useState(false);
	const [showAllMachines, setShowAllMachines] = useState(false);
	// Which machine's "popular parts" list is expanded. Only one at a time: a site can have 36
	// machines and opening them all turns the panel into an unreadable wall.
	const [openTopMachine, setOpenTopMachine] = useState(null);
	const [showAllTopMachines, setShowAllTopMachines] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			if (!selectedSite) { setLoading(false); return; }
			setLoading(true);
			setFailed(false);
			try {
				// Both calls are independent; a failure in one must not blank the other.
				const [cov, reorder, topParts] = await Promise.allSettled([
					// The whole gap list, not a page of it. Machine rows expand to show which parts are
					// missing, filtered from this response, so a partial list meant an expansion showed
					// some of a machine's parts while appearing to show all of them. Coventry — 30
					// machines, the largest site in the data — returns 230 rows in total, so this is
					// tens of kilobytes, and the panel still renders only four machines.
					getCriticalCoverage(selectedSite, 250),
					// 25, not 5. The panel still shows 5; "Show all" reveals the rest without a second
					// request. AMF asked for a way to see the full list on both panels.
					getDueForReorder(selectedSite, 25),
					// Per-machine popular parts, ranked by peer adoption on the same machine MODEL.
					// Independent of the other two: this one is the slowest call on the panel (it is a
					// live scan of order history, cached 10 minutes server-side), so it must never be
					// able to delay or blank the two that already worked.
					getMachineTopParts(selectedSite, 10),
				]);
				if (cancelled) return;
				setCoverage(cov.status === "fulfilled" && cov.value?.success ? cov.value : null);
				setDue(reorder.status === "fulfilled" && reorder.value?.success ? (reorder.value.due || []) : []);
				setTopByMachine(
					topParts.status === "fulfilled" && topParts.value?.success ? (topParts.value.machines || []) : [],
				);
				if (cov.status === "rejected" && reorder.status === "rejected") setFailed(true);
			} catch (_e) {
				if (!cancelled) setFailed(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		load();
		return () => { cancelled = true; };
	}, [selectedSite]);

	const hasCoverage = coverage && coverage.total > 0;
	const hasDue = due.length > 0;
	const hasTopParts = topByMachine.some((m) => (m.parts || []).length > 0);
	// The tile's badge count: due-for-reorder rows plus machines with at least one never-ordered RSPL
	// part. Reported whenever it changes, independent of expanded, so the badge is accurate even while
	// the panel underneath it is collapsed.
	useEffect(() => {
		if (!onCountChange || loading) return;
		const gapMachines = coverage?.by_machine?.filter((m) => m.gap > 0).length || 0;
		// Machine models carrying popular-parts recommendations count too. Counted as MODELS, not
		// parts, to match how the coverage half already contributes machines rather than its ~230
		// individual gap rows — otherwise this one section would swamp the badge on its own.
		const topModels = topByMachine.filter((m) => (m.parts || []).length > 0).length;
		onCountChange(due.length + gapMachines + topModels);
	}, [due, coverage, topByMachine, loading, onCountChange]);

	// Data still loads while collapsed (for the badge above) — only the panel itself is gated.
	if (!panelOpen) return null;

	if (loading) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3, px: 2 }}>
				<CircularProgress size={18} />
				<Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
					{t("UI_TEXT.LOADING_RECOMMENDATIONS", "Loading recommendations…")}
				</Typography>
			</Box>
		);
	}

	if (failed) {
		return <Alert severity="info" sx={{ mx: 2, my: 2 }}>
			{t("UI_TEXT.NO_RECOMMENDATIONS", "Recommendations are unavailable right now.")}
		</Alert>;
	}

	// While panelOpen is false this component already returned null above, before ever reaching here —
	// that's the "don't clutter the home page on load" rule from the part-page panel. But once the user
	// has explicitly clicked the Recommendations tile and opened this panel, showing nothing at all
	// reads as broken rather than "nothing to report" — so an open panel with no data gets a real
	// empty state instead of silently rendering blank.
	if (!hasCoverage && !hasDue && !hasTopParts) {
		return (
			<Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
				<Alert severity="info">
					{t("UI_TEXT.NO_RECOMMENDATIONS_FOR_SITE", "No recommendations for your site right now.")}
				</Alert>
			</Box>
		);
	}

	const totalCount = due.length
		+ (coverage?.by_machine?.filter((m) => m.gap > 0).length || 0)
		+ topByMachine.filter((m) => (m.parts || []).length > 0).length;

	// A plain export of the two panels above, so a maintenance manager can walk the parts room with
	// a printout instead of a phone — same client-side xlsx pattern as OrderHistory.js/
	// AnalyticsTables.js. Blank "In stock?" column is deliberate: the whole point is that this is
	// checked against physical inventory, not against anything the app already knows.
	const handleDownloadRecommendations = () => {
		const workbook = XLSX.utils.book_new();
		if (hasDue) {
			const dueSheet = XLSX.utils.json_to_sheet(due.map((d) => ({
				"Part Number": d.product_number,
				"Description": d.description || "",
				"RSPL": d.rspl || "",
				"Last Ordered (months ago)": d.months_since ?? "",
				"Typical Reorder Cycle (months)": d.typical_cycle_months ?? "",
				"In Stock?": "",
			})));
			XLSX.utils.book_append_sheet(workbook, dueSheet, "Due for Reorder");
		}
		if (hasCoverage) {
			const machineName = Object.fromEntries(
				(coverage.by_machine || []).map((m) => [m.machine_number, m.machine_name || m.machine_number])
			);
			const gapSheet = XLSX.utils.json_to_sheet((coverage.gaps || []).map((g) => ({
				"Machine": machineName[g.machine_number] || g.machine_number,
				"Part Number": g.product_number,
				"Description": g.description || "",
				"In Stock?": "",
			})));
			XLSX.utils.book_append_sheet(workbook, gapSheet, "RSPL Parts Not Purchased");
		}
		if (hasTopParts) {
			// Flattened one row per (model, part): a spreadsheet is checked against physical stock line
			// by line, so nesting parts under a machine header would make it unusable for that.
			const topSheet = XLSX.utils.json_to_sheet(
				topByMachine.flatMap((m) => (m.parts || []).map((p) => ({
					"Machine": m.machine_name || m.machine_number,
					"Part Number": p.product_number,
					"Description": p.description || "",
					"RSPL": p.rspl || "",
					// The server has already applied the under-five-bakeries rule, so this is blank
					// rather than a small exact count. Never substitute the raw number here.
					"Other Bakeries Ordering": p.peer_sites ?? "",
					"Ordered Here Before": p.never_ordered_here ? "No" : "Yes",
					"In Stock?": "",
				})))
			);
			XLSX.utils.book_append_sheet(workbook, topSheet, "Popular For Your Machines");
		}
		XLSX.writeFile(workbook, `AMF_Recommendations${selectedSite ? `_${selectedSite}` : ""}.xlsx`);
	};

	return (
		<Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
			<Box sx={{
				display: "flex", alignItems: "flex-start", justifyContent: "space-between",
				gap: 2, mb: 2.5, pb: 2, borderBottom: "2px solid var(--primary-main)",
			}}>
				<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
					<TipsAndUpdates sx={{ color: "var(--primary-main)", fontSize: 28, mt: 0.3 }} />
					<Box>
						<Typography sx={{ fontWeight: 700, fontSize: "20px", letterSpacing: "0.5px" }}>
							{t("UI_TEXT.RECOMMENDATIONS", "RECOMMENDATIONS")}
						</Typography>
						<Typography sx={{ fontSize: "12.5px", color: "text.secondary" }}>
							{t("UI_TEXT.RECOMMENDATIONS_HEADER_SUB",
								"Parts that AMF has identified that you order frequently and we would recommend ordering.")}
						</Typography>
					</Box>
				</Box>
				<Stack direction="row" spacing={1.2} alignItems="center" sx={{ flexShrink: 0 }}>
					{(hasDue || hasCoverage || hasTopParts) && (
						<Tooltip title="Download these lists as an Excel file to check against stock in your parts room">
							<Button size="small" variant="outlined"
								startIcon={<FileDownloadOutlined fontSize="small" />}
								onClick={handleDownloadRecommendations}>
								{t("UI_TEXT.DOWNLOAD", "Download")}
							</Button>
						</Tooltip>
					)}
					{totalCount > 0 && (
						// This count deliberately excludes fully-ordered/no-gap machines (a machine
						// showing "All ordered" in the RSPL panel below, or with zero popular-part
						// recommendations, doesn't add to it) — it's meant as an "items that need your
						// attention" count, not a total of every machine tracked on this page. That's
						// why it can read lower than adding up every "Show all N" link on this page by
						// eye; the tooltip says so rather than leaving it looking like a miscount.
						<Tooltip title="Parts due for reorder, plus machines with an actual RSPL gap or popular-part recommendation — fully-ordered machines aren't counted">
							<Box sx={{
								minWidth: "36px", height: "36px", px: 1, borderRadius: "18px",
								backgroundColor: "var(--primary-main)", color: "#fff",
								display: "flex", alignItems: "center", justifyContent: "center",
								fontSize: "16px", fontWeight: 700, flexShrink: 0,
							}}>
								{totalCount}
							</Box>
						</Tooltip>
					)}
				</Stack>
			</Box>
			{/* alignItems was "stretch" (the flex default), which forces all three sibling Paper
			    cards to match the tallest one's height on every render — so expanding a single
			    machine row inside "Popular for your machines" (which grows only that card's own
			    content) stretched the other two cards' empty space to match, reading as the whole
			    row unexpectedly resizing. "flex-start" lets each card size to its own content
			    instead; the tradeoff is the three cards no longer share a bottom edge when their
			    content lengths differ even at rest, but nothing shifts/stretches on expand anymore. */}
			<Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} alignItems="flex-start">

				{hasDue && (
					<Paper elevation={0} sx={{ flex: 1, p: 2, border: "1px solid #e6e8eb", borderTop: "3px solid var(--primary-main)" }}>
						<SectionHeading
							title={t("UI_TEXT.DUE_FOR_REORDER", "Due for reorder")}
							subtitle={t("UI_TEXT.DUE_FOR_REORDER_SUB", "Based on how often you have reordered these parts")}
						/>
						{(showAllDue ? due : due.slice(0, 5)).map((d) => (
							<Box key={d.product_number}
								onClick={openPart(d.machine_id, d.product_id, d) || undefined}
								sx={{ ...rowSx(openPart(d.machine_id, d.product_id, d)),
									display: "flex", alignItems: "center", gap: 1, py: 0.9,
									borderBottom: "1px solid #f0f2f4" }}>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography sx={{ fontSize: "12.5px", fontWeight: 600 }}>
										{d.description || d.product_number}
									</Typography>
									{/* The PART NUMBER, always — not only as a fallback when the description is
									    missing. Two of this bakery's due parts, HANSA193H27-1/2 and
									    HANSA125.5H27-1/2, are both described "Bun Blades", so the panel showed
									    what looked like the same row twice, with two different cycles and nothing
									    to tell them apart. Reported 2026-08-17. The coverage rows below already
									    carried the number for the same reason.

									    The reason states BOTH elapsed time and the bakery's own cycle — AMF's
									    review: "it is due for replacement per our typical use rate at 12 months
									    or whatever the wear rate cycle is for that part". */}
									<Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
										{d.product_number}
										{d.reason && d.months_since != null && d.typical_cycle_months != null ? (
											<>
												{" · Last ordered "}
												<u>{d.months_since} months</u>
												{" ago; your bakery typically reorders every "}
												<u>{d.typical_cycle_months} months</u>
											</>
										) : (d.reason ? ` · ${d.reason}` : "")}
									</Typography>
								</Box>
								{d.rspl && (
									<Chip label={d.rspl} size="small"
										sx={{ height: 18, fontSize: "9.5px", fontWeight: 700,
											backgroundColor: d.rspl === "Critical" ? "rgba(237,31,39,0.10)" : "#f0f2f5",
											color: d.rspl === "Critical" ? "var(--primary-main)" : "#555" }} />
								)}
								<PriceAndCartAction row={d} selectedSite={selectedSite} />
							</Box>
						))}
						{due.length > 5 && (
							<Link component="button" type="button" underline="hover"
								onClick={() => setShowAllDue(!showAllDue)}
								sx={{ fontSize: "11.5px", mt: 1, color: "var(--primary-main)" }}>
								{showAllDue ? "Show fewer" : `Show all ${due.length}`}
							</Link>
						)}
					</Paper>
				)}

				{hasCoverage && (
					<Paper elevation={0} sx={{ flex: 1, p: 2, border: "1px solid #e6e8eb", borderTop: "3px solid var(--primary-main)" }}>
						<SectionHeading
							title={t("UI_TEXT.CRITICAL_SPARES_COVERAGE", "RSPL Parts that You Haven't Purchased")}
							subtitle={t("UI_TEXT.CRITICAL_SPARES_COVERAGE_SUB",
								"Parts AMF designates as either Wear Parts or Critical Parts for your machines.")}
						/>
						<Typography sx={{ fontSize: "12.5px", mb: 1.4 }}>
							{t("UI_TEXT.CRITICAL_SPARES_HELD", {
								defaultValue: "Below are the parts that you did not order on your original RSPL's for your machines.",
							})}
						</Typography>
						{(showAllMachines ? coverage.by_machine : coverage.by_machine.slice(0, 4)).map((m) => {
							// The gap list for THIS machine. The API returns gaps site-wide, each tagged with the
							// machine it belongs to, so grouping happens here rather than in a second request.
							const machineGaps = (coverage.gaps || []).filter((g) => g.machine_number === m.machine_number);
							const isOpen = expanded === m.machine_number;
							return (
								<Box key={m.machine_name} sx={{ borderBottom: "1px solid #f0f2f4" }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										{/* A real button, with a word next to the chevron.
										    The bare chevron carried no accessible name at all — a screen
										    reader announced nothing — and gave no hint that a row opened.
										    AMF: "instead having chevron can be good". */}
										<Box component="button" type="button"
											aria-expanded={isOpen}
											aria-label={`${isOpen ? "Hide" : "Show"} the ${
												m.gap === 0 ? "all-ordered" : `${m.gap} never-ordered`
											} RSPL parts for ${m.machine_name || m.machine_number}`}
											onClick={() => setExpanded(isOpen ? null : m.machine_number)}
											sx={{ ...rowSx(true), flex: 1, display: "flex", alignItems: "center", gap: 1, py: 0.6,
												minWidth: 0, border: 0, background: "none", textAlign: "left",
												font: "inherit", p: 0, pr: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography noWrap sx={{ fontSize: "12px", fontWeight: 600 }}>
													{m.machine_name || m.machine_number}
												</Typography>
												{/* The machine NUMBER as well as the name: two machines at this site both read
												    "HS 40 Bulk Packer, Sealing Section" and are otherwise indistinguishable.
												    "Critical spares" is named here rather than left implied — a bare
												    "0 of 49 ordered" was read as though the site had ordered nothing at
												    all, when it means none of the 49 parts AMF designates Critical. */}
												{/* Edge cases spelled out rather than left to the numbers to imply. "0 of 4"
												    reads as a fraction someone has to do the subtraction on; "no Critical
												    spares ordered" says the same thing directly. Likewise a gap of 0 read
												    as "0 never ordered" — technically correct, but a customer parses "0"
												    before "never ordered" and the negative-of-a-negative is confusing. */}
												<Typography sx={{ fontSize: "10.5px", color: "text.secondary" }}>
													{m.machine_number} · {m.held === 0
														? "No RSPL parts ordered"
														: `${m.held} of ${m.critical_total} RSPL parts ordered`}
												</Typography>
											</Box>
											<Chip label={m.gap === 0 ? "All ordered" : `${m.gap} never ordered`} size="small"
												sx={{ height: 18, fontSize: "9.5px", fontWeight: 700,
													backgroundColor: m.gap > 0 ? "rgba(237,31,39,0.10)" : "#eaf6ec",
													color: m.gap > 0 ? "var(--primary-main)" : "#2f7d43" }} />
											<Typography sx={{ fontSize: "10.5px", color: "var(--primary-main)",
												display: "flex", alignItems: "center", flexShrink: 0 }}>
												{isOpen ? "Hide" : "Show"}
												{isOpen ? <ExpandLess sx={{ fontSize: 16 }} />
														: <ExpandMore sx={{ fontSize: 16 }} />}
											</Typography>
										</Box>
										{/* UAT 2026-08-17 (item 3): a jump icon to open the machine itself, separate from
										    the expand/collapse toggle above — the same OpenInNew pattern BomPathView uses
										    to jump to a related item. Has to be a sibling of the toggle button rather than
										    nested inside it: a <button> cannot contain another interactive element. */}
										{openMachine(m.machine_id) && (
											<Tooltip title={`Open ${m.machine_name || m.machine_number}`}>
												<IconButton size="small" onClick={openMachine(m.machine_id)}>
													<OpenInNew fontSize="inherit" />
												</IconButton>
											</Tooltip>
										)}
									</Box>
									<Collapse in={isOpen} unmountOnExit>
										<Box sx={{ pl: 1.5, pb: 1 }}>
											{machineGaps.length === 0 && (
												<Typography sx={{ fontSize: "11px", color: "text.secondary", py: 0.8 }}>
													{t("UI_TEXT.NO_GAP_DETAIL", "Open the machine to see its parts.")}
												</Typography>
											)}
											{machineGaps.map((g) => (
												<Box key={g.product_number}
													onClick={openPart(g.machine_id, g.product_id, g) || undefined}
													sx={{ ...rowSx(openPart(g.machine_id, g.product_id, g)),
														display: "flex", alignItems: "center", gap: 1, py: 0.5, pr: 1 }}>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography sx={{ fontSize: "11.5px", fontWeight: 600 }}>
															{g.description || g.product_number}
														</Typography>
														<Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
															{g.product_number}
															{g.peer_sites_holding ? ` · held by ${g.peer_sites_holding} other bakeries running this machine` : ""}
														</Typography>
													</Box>
													<PriceAndCartAction row={g} selectedSite={selectedSite} stacked />
												</Box>
											))}
											{m.gap > machineGaps.length && (
												<Typography sx={{ fontSize: "10.5px", color: "text.secondary", pt: 0.6 }}>
													{/* Only reachable if a site has more gaps than the request returns. Says what it
													    means rather than linking to the machine's FULL parts list, which was the
													    previous behaviour: the link promised 31 parts and opened all 1,387. */}
													Showing {machineGaps.length} of {m.gap} — open the machine for the rest.
												</Typography>
											)}
										</Box>
									</Collapse>
								</Box>
							);
						})}
						{coverage.by_machine.length > 4 && (
							<Link component="button" type="button" underline="hover"
								onClick={() => setShowAllMachines(!showAllMachines)}
								sx={{ fontSize: "11.5px", mt: 1, color: "var(--primary-main)" }}>
								{showAllMachines
									? "Show fewer"
									: `Show all ${coverage.by_machine.length} machines`}
							</Link>
						)}
					</Paper>
				)}


				{hasTopParts && (
					<Paper elevation={0} sx={{ flex: 1, p: 2, border: "1px solid #e6e8eb", borderTop: "3px solid var(--primary-main)" }}>
						<SectionHeading
							title={t("UI_TEXT.POPULAR_FOR_MACHINES", "Popular for your machines")}
							subtitle={t("UI_TEXT.POPULAR_FOR_MACHINES_SUB",
								"Most ordered by other bakeries running a similar machine")}
						/>
						{/* Grouped by MACHINE, collapsed. A site can own 36 machines; a flat list of ten parts
						    each would be 360 rows and would bury the two panels beside it. One machine open at a
						    time, matching how the coverage panel below handles the same problem. */}
						{(showAllTopMachines ? topByMachine : topByMachine.slice(0, 4)).map((m) => {
							const isOpen = openTopMachine === m.machine_name;
							const parts = m.parts || [];
							if (!parts.length) return null;
							return (
								<Box key={m.machine_name} sx={{ borderBottom: "1px solid #f0f2f4" }}>
									<Box
										onClick={() => setOpenTopMachine(isOpen ? null : m.machine_name)}
										sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.9, cursor: "pointer" }}>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography sx={{ fontSize: "12.5px", fontWeight: 600 }}>
												{m.machine_name || m.machine_number}
											</Typography>
											<Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
												{/* Rows are per MACHINE MODEL, not per installed unit: the recommendation is
												    derived from the model, so three identical mixers would otherwise print the
												    same list three times. When the site runs more than one, say so instead of
												    showing one arbitrary serial as though it were the only one. */}
												{m.unit_count > 1
													? `${m.unit_count} ${t("UI_TEXT.UNITS_AT_SITE", "units at your site")}`
													: m.machine_number}
												{` · ${parts.length} ${t("UI_TEXT.POPULAR_PARTS_COUNT", "commonly ordered parts")}`}
											</Typography>
										</Box>
										<Typography sx={{ fontSize: "11.5px", color: "var(--primary-main)", flexShrink: 0 }}>
											{isOpen ? t("UI_TEXT.HIDE", "Hide") : t("UI_TEXT.VIEW", "View")}
										</Typography>
									</Box>
									{isOpen && parts.map((p) => (
										<Box key={p.product_number}
											onClick={openPart(m.machine_id, p.product_id, p) || undefined}
											sx={{ ...rowSx(openPart(m.machine_id, p.product_id, p)),
												display: "flex", alignItems: "center", gap: 1, py: 0.8, pl: 1.5,
												borderTop: "1px solid #f7f8f9" }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
													{p.description || p.product_number}
												</Typography>
												{/* Part number always shown, and the reason sentence verbatim from the
												    server -- it already applies the under-five-bakeries rule, so the
												    count is only present when it is safe to state. Never rebuild that
												    sentence here from p.peer_sites. */}
												<Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
													{p.product_number}{p.reason ? ` · ${p.reason}` : ""}
												</Typography>
											</Box>
											{p.never_ordered_here && (
												<Chip label={t("UI_TEXT.NOT_YET_ORDERED", "Not yet ordered")} size="small"
													sx={{ height: 18, fontSize: "9.5px", fontWeight: 700,
														backgroundColor: "#f0f2f5", color: "#555" }} />
											)}
											{p.rspl && (
												<Chip label={p.rspl} size="small"
													sx={{ height: 18, fontSize: "9.5px", fontWeight: 700,
														backgroundColor: p.rspl === "Critical" ? "rgba(237,31,39,0.10)" : "#f0f2f5",
														color: p.rspl === "Critical" ? "var(--primary-main)" : "#555" }} />
											)}
											<PriceAndCartAction row={p} selectedSite={selectedSite} />
										</Box>
									))}
								</Box>
							);
						})}
						{topByMachine.length > 4 && (
							<Link component="button" type="button" underline="hover"
								onClick={() => setShowAllTopMachines(!showAllTopMachines)}
								sx={{ fontSize: "11.5px", mt: 1, color: "var(--primary-main)" }}>
								{showAllTopMachines
									? t("UI_TEXT.SHOW_FEWER", "Show fewer")
									: `${t("UI_TEXT.SHOW_ALL", "Show all")} ${topByMachine.length}`}
							</Link>
						)}
					</Paper>
				)}
			</Stack>

			{/* Opened by a row whose part has no machine, so no part page and no search result exists
			    for it — see openPart. Mounted only while such a row is selected, so the form is not
			    sitting in the DOM on every home page load. */}
			{contactFor && (
				<ContactCCRForm
					open
					setOpen={(next) => { if (!next) setContactFor(null); }}
					showEmergencyNotice={false}
					prefill={{
						// Machine Number is a REQUIRED field in ccr mode
						// (ContactCCRForm: `if (!isPremium && !formData.machine_number?.trim())`),
						// and these parts have no machine — that is the entire reason this form is
						// what the row opens. Left empty, the customer could not submit and would be
						// stuck at a validation error with nothing they could truthfully type.
						// Stating it plainly also tells the CCR the one thing they need to know.
						// Switching to premium mode would skip the requirement but mislabel the
						// enquiry as an AMF Premium request.
						machine_number: t("UI_TEXT.NOT_MACHINE_SPECIFIC", "Not linked to a machine"),
						subject: t("UI_TEXT.PART_ENQUIRY_SUBJECT", {
							partNumber: contactFor.product_number,
							defaultValue: `Part enquiry: ${contactFor.product_number}`,
						}),
						message: t("UI_TEXT.PART_ENQUIRY_MESSAGE", {
							partNumber: contactFor.product_number,
							description: contactFor.description || contactFor.product_number,
							defaultValue:
								`I would like to order ${contactFor.description || contactFor.product_number} `
								+ `(part ${contactFor.product_number}). It is not available to order online for `
								+ `you — could you confirm availability and pricing?`,
						}),
					}}
				/>
			)}
		</Box>
	);
};

export default SparesInsights;
